import { FC, useEffect, useMemo, useState } from 'react'

import { Button } from '@inno-ui/button'
import { DropdownOption, DropdownValue } from '@inno-ui/dropdown'
import { captureException } from '@sentry/react'
import {
  DESKTOP_SESSION_VERSION,
  serializeDesktopSession
} from 'modules/desktop/pages/MainPage/stores/windowSessionPersistence'
import { useWindowStore } from 'modules/desktop/pages/MainPage/stores/windowStore'
import { Trans, useTranslation } from 'react-i18next'
import { appConfigs } from 'shared/configs/appConfigs'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { useUserContext } from 'shared/hooks/useUserContext'
import { logger } from 'shared/services/logger'
import {
  deleteAllCaches,
  getCachedItemCount,
  nukeServiceWorkersAndCaches,
  setServiceWorkerAppLimit,
  startServiceWorkers
} from 'shared/services/registerServiceWorkers'
import { useDesktopUtilityWindowsStore } from 'shared/stores/desktopUtilityWindowsStore'
import {
  AppOpeningMode,
  ShortcutClickMethod,
  usePreferencesStore
} from 'shared/stores/preferencesStore'

import { UtilityWindowWrapper } from '../UtilityWindowWrapper'
import styles from './PreferencesWindow.module.scss'
import { PreferencesWindowProps } from './PreferencesWindow.types'
import {
  CachingReloadPromptModal,
  PreferenceDropdownField,
  PreferenceMultiSelectField,
  PreferenceToggleField
} from './components'

export const PreferencesWindow: FC<PreferencesWindowProps> = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.preferences'
  })
  const [entryCount, setEntryCount] = useState<number | null>(null)
  const [showReloadPrompt, setShowReloadPrompt] = useState(false)
  const [cachedItemCount, setCachedItemCount] = useState<number | null>(null)
  const [isDeletingCaches, setIsDeletingCaches] = useState(false)

  const { user } = useUserContext()

  useEffect(() => {
    logger.getEntryCount().then(setEntryCount)
  }, [])
  const isPreferencesWindowOpen = useDesktopUtilityWindowsStore(
    state => state.activeWindow === 'preferences'
  )

  useEffect(() => {
    if (!isPreferencesWindowOpen) return
    getCachedItemCount().then(setCachedItemCount)
  }, [isPreferencesWindowOpen])
  const closeWindow = useDesktopUtilityWindowsStore(state => state.closeWindow)
  const defaultAppOpeningMode = usePreferencesStore(
    state => state.defaultAppOpeningMode
  )
  const setDefaultAppOpeningMode = usePreferencesStore(
    state => state.setDefaultAppOpeningMode
  )
  const shortcutClickMethod = usePreferencesStore(
    state => state.shortcutClickMethod
  )
  const setShortcutClickMethod = usePreferencesStore(
    state => state.setShortcutClickMethod
  )
  const desktopSessionInUrlEnabled = usePreferencesStore(
    state => state.desktopSessionInUrlEnabled
  )
  const setDesktopSessionInUrlEnabled = usePreferencesStore(
    state => state.setDesktopSessionInUrlEnabled
  )
  const cachingEnabled = usePreferencesStore(state => state.cachingEnabled)
  const setCachingEnabled = usePreferencesStore(
    state => state.setCachingEnabled
  )
  const cachedAppIds = usePreferencesStore(state => state.cachedAppIds)
  const setCachedAppIds = usePreferencesStore(state => state.setCachedAppIds)

  const handleClose = () => {
    closeWindow()
  }

  const handleSendSentryTestEvent = () => {
    const marker = `SENTRY_Q3_PRIVACY_TEST_${new Date().toISOString()}`
    captureException(new Error(marker))
  }

  const openingModeOptions = useMemo<DropdownOption[]>(
    () => [
      { label: t('open_apps_by_default.modes.windowed'), value: 'windowed' },
      { label: t('open_apps_by_default.modes.maximized'), value: 'maximized' },
      { label: t('open_apps_by_default.modes.detached'), value: 'detached' }
    ],
    [t]
  )

  const handleOpeningModeChange = (value: DropdownValue) => {
    const selectedMode = (value as DropdownOption).value as AppOpeningMode
    setDefaultAppOpeningMode(selectedMode)
  }

  const shortcutClickOptions = useMemo<DropdownOption[]>(
    () => [
      { label: t('shortcut_click_method.modes.single'), value: 'single' },
      { label: t('shortcut_click_method.modes.double'), value: 'double' }
    ],
    [t]
  )

  const handleShortcutClickMethodChange = (value: DropdownValue) => {
    const selectedMethod = (value as DropdownOption)
      .value as ShortcutClickMethod
    setShortcutClickMethod(selectedMethod)
  }

  const handleDesktopSessionInUrlToggle = async (checked: boolean) => {
    setDesktopSessionInUrlEnabled(checked)

    const storage = useWindowStore.persist.getOptions().storage as ReturnType<
      typeof useWindowStore.persist.getOptions
    >['storage']

    if (!storage) return

    if (!checked) {
      await storage.removeItem('desktop')
      return
    }

    await storage.setItem('desktop', {
      state: serializeDesktopSession(useWindowStore.getState()),
      version: DESKTOP_SESSION_VERSION
    })
  }

  const cachedAppOptions = useMemo<DropdownOption[]>(
    () =>
      Object.values(appConfigs).map(config => ({
        label: config.label,
        value: config.id
      })),
    []
  )

  const selectedCachedAppOptions = useMemo<DropdownOption[]>(
    () =>
      cachedAppOptions.filter(option => cachedAppIds.includes(option.value)),
    [cachedAppIds, cachedAppOptions]
  )

  const handleCachingToggle = async (checked: boolean) => {
    setCachingEnabled(checked)
    if (checked) {
      startServiceWorkers(cachedAppIds)
    } else {
      await nukeServiceWorkersAndCaches()
      setShowReloadPrompt(true)
    }
  }

  const handleCachedAppsChange = (selected: DropdownOption[]) => {
    const ids = selected.map(option => option.value)
    setCachedAppIds(ids)
    if (cachingEnabled) {
      setServiceWorkerAppLimit(ids)
    }
  }

  const handleDeleteCaches = async () => {
    setIsDeletingCaches(true)
    try {
      await deleteAllCaches()
      const count = await getCachedItemCount()
      setCachedItemCount(count)
    } finally {
      setIsDeletingCaches(false)
    }
  }

  const deleteCachesLabel =
    cachedItemCount && cachedItemCount > 0
      ? t('delete_caches.with_count', { count: cachedItemCount })
      : t('delete_caches.empty')

  return (
    <UtilityWindowWrapper
      isOpen={isPreferencesWindowOpen}
      title={t('title')}
      onClose={handleClose}
    >
      <div className={styles.preferencesWindow}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sections.window')}</h3>
          <PreferenceDropdownField
            label={t('open_apps_by_default.label')}
            hint={
              <p className={styles.tooltipOverlay}>
                <Trans
                  t={t}
                  i18nKey='open_apps_by_default.tooltip'
                  components={{
                    b: <b />
                  }}
                />
              </p>
            }
            items={openingModeOptions}
            value={
              FEATURE_FLAGS.FORCE_DETACHED_MODE
                ? 'detached'
                : defaultAppOpeningMode
            }
            onChange={handleOpeningModeChange}
            disabled={FEATURE_FLAGS.FORCE_DETACHED_MODE}
            note={
              FEATURE_FLAGS.FORCE_DETACHED_MODE &&
              t('open_apps_by_default.forced_notice')
            }
          />
          <PreferenceDropdownField
            label={t('shortcut_click_method.label')}
            hint={
              <p className={styles.tooltipOverlay}>
                <Trans
                  t={t}
                  i18nKey='shortcut_click_method.tooltip'
                  components={{
                    b: <b />
                  }}
                />
              </p>
            }
            items={shortcutClickOptions}
            value={shortcutClickMethod}
            onChange={handleShortcutClickMethodChange}
          />
          <PreferenceToggleField
            label={t('restore_desktop_after_refresh.label')}
            hint={
              <p className={styles.tooltipOverlay}>
                <Trans
                  t={t}
                  i18nKey='restore_desktop_after_refresh.tooltip'
                  components={{
                    b: <b />
                  }}
                />
              </p>
            }
            checked={desktopSessionInUrlEnabled}
            onChange={handleDesktopSessionInUrlToggle}
          />
        </section>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('sections.caching')}</h3>
          <PreferenceToggleField
            label={t('caching.label')}
            hint={
              <p className={styles.tooltipOverlay}>
                <Trans
                  t={t}
                  i18nKey='caching.tooltip'
                  components={{
                    b: <b />
                  }}
                />
              </p>
            }
            checked={cachingEnabled}
            onChange={handleCachingToggle}
          />
          <PreferenceMultiSelectField
            label={t('cached_apps.label')}
            hint={
              <p className={styles.tooltipOverlay}>
                <Trans
                  t={t}
                  i18nKey='cached_apps.tooltip'
                  components={{
                    b: <b />
                  }}
                />
              </p>
            }
            items={cachedAppOptions}
            values={selectedCachedAppOptions}
            onChange={handleCachedAppsChange}
            placeholder={t('cached_apps.placeholder')}
            disabled={!cachingEnabled}
          />
          <div className={styles.cacheActions}>
            <Button
              onClick={handleDeleteCaches}
              isLoading={isDeletingCaches}
              disabled={!cachedItemCount || isDeletingCaches}
              label={deleteCachesLabel}
            />
          </div>
        </section>
        {user?.organization_id === INNOSCRIPTA_INTERNAL_ORGANIZATION_ID && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{t('sections.logs')}</h3>
            <div className={styles.logControls}>
              <Button
                onClick={() => logger.export()}
                label={`${t('downloadLogs')} ${
                  entryCount != null ? ` (${entryCount} entries)` : ''
                }`}
              />
            </div>
          </section>
        )}
        {process.env.REACT_APP_ENVIRONMENT === 'testing' && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Testing tools</h3>
            <div className={styles.logControls}>
              <Button
                onClick={handleSendSentryTestEvent}
                label='Send Sentry test event'
              />
            </div>
          </section>
        )}
      </div>
      <CachingReloadPromptModal
        isOpen={showReloadPrompt}
        onClose={() => setShowReloadPrompt(false)}
      />
    </UtilityWindowWrapper>
  )
}
